import { useEffect, useId, useState } from "react";
import { Head } from "@unhead/react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../analytics/track";
import api from "../api";

const TRIAL_LINE =
  "$0 today · 1 month free · then $1.99/month · cancel anytime";

const TRUST_POINTS = [
  {
    title: "Uses your Strike account",
    body: "Connect securely with OAuth. No separate exchange account is required.",
  },
  {
    title: "Non-custodial",
    body: "SatStacker never holds your cash or Bitcoin.",
  },
  {
    title: "Built for DCA",
    body: "No leverage. No trading screen. Just automated Bitcoin accumulation.",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Create your account",
    body: "Start your free month and choose the amount and cadence for your Bitcoin DCA.",
  },
  {
    number: "02",
    title: "Connect Strike",
    body: "Link your existing Strike account securely through OAuth.",
  },
  {
    number: "03",
    title: "Let Smart Timing work",
    body: "SatStacker looks for a better Bitcoin price within each buy window and automates the purchase.",
  },
];

function SmartTimingHeroChart() {
  const uid = useId();
  const areaId = `paidSmartArea-${uid.replace(/:/g, "")}`;

  // Curated, illustrative BTC-like path. This is not live or historical data.
  // The visual mirrors SatStacker's logged-out dashboard explainer:
  // gray = scheduled DCA at the end of each buy window, orange = a Smart Timing entry inside that window.
  const prices = [
    100.0, 100.6, 99.9, 100.4, 101.1, 101.6, 100.8,
    102.0, 100.8, 99.4, 100.0, 98.5, 97.4, 97.9,
    97.0, 96.1, 94.8, 94.2, 95.0, 95.9, 96.7,
    97.5, 97.0, 95.5, 96.9, 98.2, 99.0, 99.7, 100.3,
  ];

  const W = 460;
  const H = 224;
  const PAD = { l: 18, r: 18, t: 16, b: 34 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  const x = (i) =>
    PAD.l + (i / (prices.length - 1)) * chartW;
  const y = (value) =>
    PAD.t + (1 - (value - min) / range) * chartH;

  const pts = prices.map((price, i) => ({
    i,
    price,
    x: x(i),
    y: y(price),
  }));

  const lineD = pts
    .map(
      (point, i) =>
        `${i === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`
    )
    .join(" ");

  const areaD =
    `${lineD} L ${x(prices.length - 1).toFixed(1)} ${H - PAD.b}` +
    ` L ${x(0).toFixed(1)} ${H - PAD.b} Z`;

  const windowStartIdx = [0, 7, 14, 21];
  const scheduledIdx = [7, 14, 21, 28];
  const smartIdx = [2, 11, 17, 24];

  const scheduled = scheduledIdx.map((i) => pts[i]);
  const smart = smartIdx.map((i) => pts[i]);

  return (
    <div className="smart-chart-wrap">
      <div className="smart-chart-legend">
        <span>
          <i className="legend-dot legend-dot-scheduled" />
          Scheduled DCA
        </span>
        <span>
          <i className="legend-dot legend-dot-smart" />
          Smart Timing
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="smart-chart-svg"
        role="img"
        aria-label="Illustrative Bitcoin price path comparing scheduled DCA buys with Smart Timing buys inside four buy windows"
      >
        <defs>
          <linearGradient
            id={areaId}
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor="#f97316"
              stopOpacity="0.14"
            />
            <stop
              offset="100%"
              stopColor="#f97316"
              stopOpacity="0"
            />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75].map((t) => {
          const yy = PAD.t + chartH * t;
          return (
            <line
              key={t}
              x1={PAD.l}
              y1={yy}
              x2={W - PAD.r}
              y2={yy}
              stroke="#27272a"
              strokeWidth="1"
              opacity="0.8"
            />
          );
        })}

        {windowStartIdx.map((idx, week) => {
          const nextIdx =
            week === windowStartIdx.length - 1
              ? prices.length - 1
              : windowStartIdx[week + 1];
          const left = x(idx);
          const right = x(nextIdx);

          return (
            <g key={`week-${week}`}>
              {week % 2 === 0 && (
                <rect
                  x={left}
                  y={PAD.t}
                  width={Math.max(0, right - left)}
                  height={chartH}
                  fill="#ffffff"
                  opacity="0.018"
                />
              )}

              <line
                x1={left}
                y1={PAD.t}
                x2={left}
                y2={H - PAD.b}
                stroke="#3f3f46"
                strokeWidth="1"
                strokeDasharray="3 6"
                opacity="0.9"
              />

              <text
                x={left + 5}
                y={H - 10}
                fontSize="10"
                fill="#71717a"
              >
                W{week + 1}
              </text>
            </g>
          );
        })}

        <path
          d={areaD}
          fill={`url(#${areaId})`}
        />

        <path
          d={lineD}
          fill="none"
          stroke="#d4d4d8"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {scheduled.map((fixed, i) => {
          const better = smart[i];
          return (
            <line
              key={`guide-${i}`}
              x1={fixed.x}
              y1={fixed.y}
              x2={better.x}
              y2={better.y}
              stroke="#f97316"
              strokeWidth="1.2"
              strokeDasharray="4 5"
              strokeOpacity="0.28"
            />
          );
        })}

        {scheduled.map((point, i) => (
          <g key={`scheduled-${i}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="5"
              fill="#71717a"
            />
            <circle
              cx={point.x}
              cy={point.y}
              r="8"
              fill="none"
              stroke="#a1a1aa"
              strokeOpacity="0.25"
            />
          </g>
        ))}

        {smart.map((point, i) => (
          <g key={`smart-${i}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="5.5"
              fill="#f97316"
            />
            <circle
              cx={point.x}
              cy={point.y}
              r="9"
              fill="none"
              stroke="#f97316"
              strokeOpacity="0.30"
            />
          </g>
        ))}
      </svg>

      <p className="smart-chart-caption">
        Illustrative example · Smart Timing looks for better prices within each buy window.
      </p>
    </div>
  );
}

function formatResultDate(value) {
  if (!value) {
    return "";
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}


function formatSats(value) {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "--";
  }

  return Math.round(n).toLocaleString();
}


function formatResultPct(value) {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return null;
  }

  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

export default function PaidTrafficLanding() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const [
    smartTimingResult,
    setSmartTimingResult,
  ] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisible(true);
    }, 60);

    trackEvent("paid_landing_view", {
      entry: "paid_landing",
      page_path:
        typeof window !== "undefined"
          ? window.location.pathname
          : "/start",
    });

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSmartTimingResult() {
      try {
        const res = await api.get(
          "/smart-timing/highlights"
        );

        const items = Array.isArray(
          res?.data?.highlights
        )
          ? res.data.highlights
          : [];

        const usable = items.filter(
          (item) =>
            Number.isFinite(
              Number(item?.more_btc_pct)
            ) &&
            Number.isFinite(
              Number(item?.extra_sats)
            ) &&
            Number.isFinite(
              Number(item?.smart_sats)
            ) &&
            Number.isFinite(
              Number(item?.scheduled_sats)
            ) &&
            (
              !item?.benchmark ||
              item.benchmark === "scheduled_window_end"
            )
        );

        if (
          cancelled ||
          usable.length === 0
        ) {
          return;
        }

        const dayNumber = Math.floor(
          Date.now() / 86400000
        );

        setSmartTimingResult(
          usable[
            dayNumber % usable.length
          ]
        );
      } catch (error) {
        console.warn(
          "[PaidLanding] Smart Timing result unavailable",
          error
        );
      }
    }

    loadSmartTimingResult();

    return () => {
      cancelled = true;
    };
  }, []);

  function startTrial() {
    // HypeLab is optimized for web signups, including on mobile browsers.
    // Direct app-store URLs lose the web UTM/first-touch context unless
    // they are replaced with a configured attribution/deep-link solution.
    trackEvent("paid_landing_cta_clicked", {
      entry: "paid_landing",
      destination: "web_signup",
      product: "smart_timing_monthly_web",
      trial: true,
      price_after_trial: 1.99,
    });

    try {
      localStorage.setItem(
        "satstacker_signup_intent",
        JSON.stringify({
          intent: "smart_timing_trial",
          source: "paid_landing",
          return_to: "/plans?smartTiming=1",
          ts: Date.now(),
        })
      );
    } catch {}

    // Preserve campaign parameters directly across the SPA navigation,
    // in addition to the existing first-touch localStorage capture.
    const incoming = new URLSearchParams(window.location.search);
    const next = new URLSearchParams();
    next.set("redirect", "/plans?smartTiming=1");

    for (const key of [
      "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
      "pid", "af_media_source", "clickid", "click_id", "oppref",
    ]) {
      const value = incoming.get(key);
      if (value) next.set(key, value);
    }

    navigate(`/signup?${next.toString()}`);
  }

  return (
    <>
      <Head>
        <title>Start Smart Timing | SatStacker</title>
        <meta
          name="description"
          content="Automate your Bitcoin DCA with Smart Timing. Connect your existing Strike account, start with one month free, and let SatStacker look for better prices within each buy window."
        />
        <meta
          name="robots"
          content="noindex,nofollow"
        />
      </Head>

      <div className="paid-page">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap');

          * {
            box-sizing: border-box;
          }

          html {
            background: #09090b;
          }

          body {
            margin: 0;
          }

          .paid-page {
            min-height: 100vh;
            overflow-x: hidden;
            background:
              radial-gradient(
                circle at 68% 24%,
                rgba(249, 115, 22, 0.10),
                transparent 34%
              ),
              #09090b;
            color: #f4f4f5;
            font-family: "DM Sans", sans-serif;
          }

          .paid-shell {
            width: min(1160px, calc(100% - 40px));
            margin: 0 auto;
          }

          .paid-header {
            min-height: 72px;
            display: flex;
            align-items: center;
            border-bottom: 1px solid rgba(255,255,255,0.06);
          }

          .paid-brand {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            color: #f4f4f5;
            text-decoration: none;
          }

          .paid-brand img {
            width: 38px;
            height: 38px;
            object-fit: contain;
          }

          .paid-brand-text {
            font-family: Georgia, "Times New Roman", serif;
            font-size: 21px;
            font-weight: 700;
          }

          .paid-hero {
            min-height: calc(100vh - 72px);
            display: grid;
            grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
            gap: 72px;
            align-items: center;
            padding: 70px 0 82px;
          }

          .paid-copy {
            opacity: 0;
            transform: translateY(20px);
            transition:
              opacity 0.7s ease,
              transform 0.7s ease;
          }

          .paid-copy.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .paid-eyebrow {
            margin: 0 0 20px;
            color: #f97316;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.20em;
            text-transform: uppercase;
          }

          .paid-title {
            margin: 0;
            max-width: 690px;
            color: #f4f4f5;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: clamp(3rem, 4.4vw, 4.25rem);
            line-height: 1.01;
            font-weight: 900;
            letter-spacing: -0.035em;
          }

          .paid-title span {
            color: #f97316;
          }

          .paid-description {
            max-width: 620px;
            margin: 28px 0 0;
            color: rgba(244,244,245,0.68);
            font-size: 18px;
            line-height: 1.7;
            font-weight: 300;
          }

          .paid-trust-line {
            margin: 22px 0 0;
            color: rgba(244,244,245,0.88);
            font-size: 14px;
            font-weight: 500;
          }

          .paid-actions {
            margin-top: 32px;
          }

          .paid-primary {
            appearance: none;
            border: 0;
            border-radius: 10px;
            background: #f97316;
            color: #09090b;
            cursor: pointer;
            padding: 16px 28px;
            font: inherit;
            font-size: 16px;
            font-weight: 700;
            box-shadow: 0 16px 50px rgba(249,115,22,0.16);
            transition:
              transform 0.16s ease,
              background 0.16s ease;
          }

          .paid-primary:hover {
            background: #fb8a2f;
            transform: translateY(-1px);
          }

          .paid-primary:active {
            transform: translateY(0);
          }

          .paid-price {
            margin: 12px 0 0;
            color: rgba(244,244,245,0.52);
            font-size: 13px;
          }

          .paid-rating {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 26px;
            color: rgba(244,244,245,0.48);
            font-size: 13px;
          }

          .paid-stars {
            color: #f97316;
            letter-spacing: 1px;
          }

          .half-star {
            background: linear-gradient(
              90deg,
              #f97316 50%,
              rgba(244, 244, 245, 0.22) 50%
            );
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
            color: transparent;
          }

          .paid-proof {
            position: relative;
            border: 1px solid rgba(249,115,22,0.24);
            border-radius: 22px;
            background:
              linear-gradient(
                180deg,
                rgba(24,24,27,0.92),
                rgba(14,14,17,0.94)
              );
            box-shadow:
              0 30px 100px rgba(0,0,0,0.34),
              inset 0 1px 0 rgba(255,255,255,0.04);
            padding: 26px;
          }

          .paid-proof-label {
            color: rgba(244,244,245,0.42);
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.16em;
            text-transform: uppercase;
          }

          .paid-proof h2 {
            margin: 10px 0 8px;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: 28px;
            line-height: 1.15;
          }

          .paid-proof-copy {
            margin: 0;
            color: rgba(244,244,245,0.58);
            font-size: 14px;
            line-height: 1.6;
          }

          .result-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 20px;
          }

          .result-subtitle {
            margin: 6px 0 0;
            color: rgba(244,244,245,0.46);
            font-size: 12px;
          }

          .result-date {
            color: rgba(244,244,245,0.38);
            font-size: 12px;
            white-space: nowrap;
          }

          .result-gain {
            margin-top: 32px;
            color: #f97316;
            font-family:
              "Space Grotesk",
              "DM Sans",
              sans-serif;
            font-size: clamp(3.4rem, 5vw, 5.2rem);
            font-weight: 700;
            letter-spacing: -0.055em;
            line-height: 0.95;
            text-align: center;
          }

          .result-gain-label {
            margin: 10px 0 0;
            color: #f4f4f5;
            font-size: 16px;
            font-weight: 600;
            text-align: center;
          }

          .result-stats {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-top: 30px;
          }

          .result-stat {
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 14px;
            background: rgba(255,255,255,0.025);
            padding: 16px;
            text-align: center;
          }

          .result-stat.smart {
            border-color: rgba(249,115,22,0.22);
            background: rgba(249,115,22,0.055);
          }

          .result-stat-label {
            color: rgba(244,244,245,0.38);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.10em;
            text-transform: uppercase;
          }

          .result-stat-value {
            margin-top: 7px;
            color: #d4d4d8;
            font-size: 18px;
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }

          .result-stat.smart
          .result-stat-value {
            color: #fb923c;
          }

          .result-disclaimer {
            margin: 17px 0 0;
            color: rgba(244,244,245,0.30);
            font-size: 10px;
            line-height: 1.5;
            text-align: center;
          }

          .explainer-section {
            padding: 86px 0 30px;
          }

          .explainer-grid {
            display: grid;
            grid-template-columns:
              minmax(260px, 0.72fr)
              minmax(420px, 1.28fr);
            gap: 64px;
            align-items: center;
          }

          .explainer-copy {
            max-width: 440px;
          }

          .explainer-copy h2 {
            margin: 0;
            font-family:
              "Space Grotesk",
              "DM Sans",
              sans-serif;
            font-size: clamp(
              2rem,
              3.4vw,
              3.2rem
            );
            line-height: 1.08;
          }

          .explainer-copy p:last-child {
            margin: 18px 0 0;
            color: rgba(244,244,245,0.54);
            font-size: 15px;
            line-height: 1.7;
          }

          .explainer-chart
          .smart-chart-wrap {
            margin-top: 0;
          }

          .window-card {
            margin-top: 24px;
            border: 1px solid rgba(255,255,255,0.08);
            border-radius: 16px;
            background: #111114;
            padding: 20px;
          }

          .window-top {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            align-items: baseline;
          }

          .window-title {
            font-size: 14px;
            font-weight: 700;
          }

          .window-status {
            color: #f97316;
            font-size: 12px;
            font-weight: 600;
          }

          .window-chart {
            position: relative;
            height: 140px;
            margin-top: 20px;
            overflow: hidden;
            border-radius: 12px;
            background:
              linear-gradient(
                to bottom,
                rgba(255,255,255,0.04) 1px,
                transparent 1px
              );
            background-size: 100% 35px;
          }

          .window-chart::before {
            content: "";
            position: absolute;
            left: -4%;
            right: -4%;
            top: 54%;
            height: 4px;
            border-radius: 999px;
            background: linear-gradient(
              90deg,
              transparent 0%,
              rgba(244,244,245,0.30) 10%,
              rgba(244,244,245,0.30) 28%,
              #f97316 48%,
              #f97316 62%,
              rgba(244,244,245,0.30) 76%,
              transparent 100%
            );
            transform: rotate(-5deg);
            box-shadow: 0 0 18px rgba(249,115,22,0.22);
          }

          .window-chart::after {
            content: "Smart Timing buy";
            position: absolute;
            left: 52%;
            top: 68%;
            transform: translateX(-50%);
            padding: 6px 9px;
            border: 1px solid rgba(249,115,22,0.28);
            border-radius: 999px;
            background: rgba(249,115,22,0.10);
            color: #fb923c;
            font-size: 10px;
            font-weight: 700;
            white-space: nowrap;
          }

          .window-footer {
            display: flex;
            justify-content: space-between;
            gap: 20px;
            margin-top: 14px;
            color: rgba(244,244,245,0.42);
            font-size: 11px;
          }

          .smart-chart-wrap {
            margin-top: 18px;
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 14px;
            background: #0b0b0d;
            padding: 14px 14px 10px;
          }

          .smart-chart-legend {
            display: flex;
            align-items: center;
            gap: 18px;
            flex-wrap: wrap;
            margin: 0 2px 6px;
            color: rgba(244,244,245,0.58);
            font-size: 11px;
            font-weight: 600;
          }

          .smart-chart-legend span {
            display: inline-flex;
            align-items: center;
            gap: 7px;
          }

          .legend-dot {
            width: 9px;
            height: 9px;
            border-radius: 999px;
            display: inline-block;
          }

          .legend-dot-scheduled {
            background: #71717a;
            box-shadow: 0 0 0 3px rgba(161,161,170,0.10);
          }

          .legend-dot-smart {
            background: #f97316;
            box-shadow: 0 0 0 3px rgba(249,115,22,0.11);
          }

          .smart-chart-svg {
            display: block;
            width: 100%;
            height: auto;
            min-height: 205px;
          }

          .smart-chart-caption {
            margin: 2px 3px 0;
            color: rgba(244,244,245,0.36);
            font-size: 10px;
            line-height: 1.45;
          }

          .paid-band {
            border-top: 1px solid rgba(255,255,255,0.06);
            border-bottom: 1px solid rgba(255,255,255,0.06);
            background: rgba(255,255,255,0.016);
          }

          .trust-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1px;
            background: rgba(255,255,255,0.06);
          }

          .trust-item {
            min-height: 150px;
            background: #0c0c0f;
            padding: 28px;
          }

          .trust-item h3 {
            margin: 0 0 9px;
            color: #f4f4f5;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: 19px;
          }

          .trust-item p {
            margin: 0;
            color: rgba(244,244,245,0.52);
            font-size: 14px;
            line-height: 1.65;
          }

          .paid-steps {
            padding: 92px 0;
          }

          .section-eyebrow {
            margin: 0 0 12px;
            color: #f97316;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.18em;
            text-transform: uppercase;
          }

          .section-title {
            max-width: 720px;
            margin: 0;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: clamp(2.2rem, 4vw, 3.6rem);
            line-height: 1.06;
          }

          .steps-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 18px;
            margin-top: 40px;
          }

          .step-card {
            border: 1px solid rgba(255,255,255,0.07);
            border-radius: 16px;
            background: rgba(255,255,255,0.025);
            padding: 26px;
          }

          .step-number {
            color: #f97316;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: 30px;
            font-weight: 900;
          }

          .step-card h3 {
            margin: 18px 0 9px;
            font-size: 17px;
          }

          .step-card p {
            margin: 0;
            color: rgba(244,244,245,0.52);
            font-size: 14px;
            line-height: 1.65;
          }

          .paid-final {
            padding: 0 0 92px;
          }

          .final-card {
            border: 1px solid rgba(249,115,22,0.22);
            border-radius: 22px;
            background:
              linear-gradient(
                135deg,
                rgba(249,115,22,0.11),
                rgba(249,115,22,0.025)
              );
            padding: 54px 30px;
            text-align: center;
          }

          .final-card h2 {
            margin: 0;
            font-family: "Space Grotesk", "DM Sans", sans-serif;
            font-size: clamp(2.2rem, 4vw, 3.4rem);
          }

          .final-card p {
            margin: 14px auto 26px;
            max-width: 560px;
            color: rgba(244,244,245,0.55);
            font-size: 15px;
            line-height: 1.65;
          }

          .paid-footer {
            border-top: 1px solid rgba(255,255,255,0.06);
            padding: 24px 0 30px;
          }

          .footer-row {
            display: flex;
            justify-content: space-between;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
            color: rgba(244,244,245,0.30);
            font-size: 12px;
          }

          .footer-links {
            display: flex;
            gap: 18px;
          }

          .footer-links a {
            color: rgba(244,244,245,0.42);
            text-decoration: none;
          }

          @media (max-width: 900px) {
            .explainer-grid {
              grid-template-columns: 1fr;
              gap: 30px;
            }

            .explainer-copy {
              max-width: 650px;
            }
            .paid-hero {
              grid-template-columns: 1fr;
              gap: 44px;
              min-height: auto;
              padding: 54px 0 62px;
            }

            .paid-proof {
              max-width: 650px;
            }

            .trust-grid,
            .steps-grid {
              grid-template-columns: 1fr;
            }

            .trust-grid {
              gap: 1px;
            }
          }

          @media (max-width: 600px) {
            .paid-shell {
              width: min(100% - 28px, 1160px);
            }

            .paid-header {
              min-height: 64px;
            }

            .paid-brand img {
              width: 34px;
              height: 34px;
            }

            .paid-brand-text {
              font-size: 19px;
            }

            .paid-hero {
              padding-top: 40px;
            }

            .paid-title {
              font-size: clamp(2.65rem, 14vw, 4rem);
            }

            .paid-description {
              font-size: 16px;
            }

            .paid-primary {
              width: 100%;
            }

            .paid-rating {
              align-items: flex-start;
              flex-direction: column;
              gap: 5px;
            }

            .paid-proof {
              padding: 20px;
            }

            .paid-steps {
              padding: 70px 0;
            }

            .final-card {
              padding: 42px 20px;
            }
          }
        `}</style>

        <header className="paid-header">
          <div className="paid-shell">
            <a
              className="paid-brand"
              href="/"
              aria-label="SatStacker home"
            >
              <img
                src="/icons/icon-192.png"
                alt=""
              />
              <span className="paid-brand-text">
                SatStacker
              </span>
            </a>
          </div>
        </header>

        <main>
          <section className="paid-shell paid-hero">
            <div
              className={
                visible
                  ? "paid-copy visible"
                  : "paid-copy"
              }
            >
              <p className="paid-eyebrow">
                Automated Bitcoin DCA
              </p>

              <h1 className="paid-title">
                Smarter Bitcoin DCA.
                <br />
                <span>Stack More Sats.</span>
              </h1>

              <p className="paid-description">
                SatStacker connects to your existing
                Strike account and looks for better
                Bitcoin prices within each buy window
                — automatically.
              </p>

              <p className="paid-trust-line">
                Uses your Strike account · SatStacker
                never holds your funds
              </p>

              <div className="paid-actions">
                <button
                  type="button"
                  className="paid-primary"
                  onClick={startTrial}
                >
                  Start your free month →
                </button>

                <p className="paid-price">
                  {TRIAL_LINE}
                </p>
              </div>

              <div className="paid-rating">
                <span
                  className="paid-stars"
                  aria-label="4.5 out of 5 stars"
                >
                  ★★★★
                  <span className="half-star">★</span>
                </span>

                <span>
                  4.5/5 — MobileAppDaily Certified
                </span>
              </div>
            </div>

            <aside
              className="paid-proof"
              aria-label="Recent Smart Timing result"
            >
              {smartTimingResult ? (
                <>
                  <div className="result-top">
                    <div>
                      <div className="paid-proof-label">
                        Recent Smart Timing result
                      </div>

                      <p className="result-subtitle">
                        Anonymous completed SatStacker buy window
                      </p>
                    </div>

                    <span className="result-date">
                      {formatResultDate(
                        smartTimingResult.completed_at
                      )}
                    </span>
                  </div>

                  <div className="result-gain">
                    {formatResultPct(
                      smartTimingResult.more_btc_pct
                    )}
                  </div>

                  <p className="result-gain-label">
                    more Bitcoin vs scheduled DCA
                  </p>

                  <div className="result-stats">
                    <div className="result-stat smart">
                      <div className="result-stat-label">
                        Smart Timing
                      </div>

                      <div className="result-stat-value">
                        {formatSats(
                          smartTimingResult.smart_sats
                        )} sats
                      </div>
                    </div>

                    <div className="result-stat">
                      <div className="result-stat-label">
                        Scheduled DCA
                      </div>

                      <div className="result-stat-value">
                        {formatSats(
                          smartTimingResult.scheduled_sats
                        )} sats
                      </div>
                    </div>
                  </div>

                  <p className="result-disclaimer">
                    +{formatSats(
                      smartTimingResult.extra_sats
                    )} extra sats in this completed window.
                    Individual results vary. Historical result,
                    not a guarantee.
                  </p>
                </>
              ) : (
                <>
                  <div className="paid-proof-label">
                    Smart Timing
                  </div>

                  <h2>
                    Same DCA schedule.
                    Smarter execution.
                  </h2>

                  <p className="paid-proof-copy">
                    SatStacker looks for better Bitcoin prices
                    within each buy window and automates the
                    purchase according to your recurring plan.
                  </p>
                </>
              )}
            </aside>
          </section>

          <section className="paid-band">
            <div className="paid-shell trust-grid">
              {TRUST_POINTS.map((item) => (
                <article
                  className="trust-item"
                  key={item.title}
                >
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="paid-shell explainer-section">
            <div className="explainer-grid">
              <div className="explainer-copy">
                <p className="section-eyebrow">
                  How Smart Timing works
                </p>

                <h2>
                  Same DCA schedule.
                  Smarter execution.
                </h2>

                <p>
                  Your regular DCA budget and cadence
                  stay the same. Smart Timing uses each
                  buy window to look for a better Bitcoin
                  price before executing.
                </p>
              </div>

              <div className="explainer-chart">
                <SmartTimingHeroChart />
              </div>
            </div>
          </section>

          <section className="paid-shell paid-steps">
            <p className="section-eyebrow">
              How it works
            </p>

            <h2 className="section-title">
              Three steps from click to automated
              Bitcoin DCA.
            </h2>

            <div className="steps-grid">
              {STEPS.map((step) => (
                <article
                  className="step-card"
                  key={step.number}
                >
                  <div className="step-number">
                    {step.number}
                  </div>

                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="paid-shell paid-final">
            <div className="final-card">
              <h2>
                Start stacking smarter.
              </h2>

              <p>
                Set up your Smart Timing plan on
                the web, connect Strike securely,
                and let SatStacker automate the
                rest.
              </p>

              <button
                type="button"
                className="paid-primary"
                onClick={startTrial}
              >
                Start your free month →
              </button>

              <p className="paid-price">
                {TRIAL_LINE}
              </p>
            </div>
          </section>
        </main>

        <footer className="paid-footer">
          <div className="paid-shell footer-row">
            <span>
              © 2026 SatStacker · Bitcoin only ·
              Not financial advice
            </span>

            <div className="footer-links">
              <a href="/privacy">
                Privacy
              </a>
              <a href="/terms">
                Terms
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
